// React
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

//Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  nextRound,
  setEvent,
  setLoadingEvent
} from '../../features/event/eventSlice';

// Store
import { doc, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';

// UI
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from '@mui/material';
import { getGameByEventId } from '../../features/game/gameSlice';
import { getBracketsByEventId } from '../../features/bracket/bracketSlice';
import { setAlertError } from '../../features/alert/alertSlice';
import { pickPrimaryBracket } from '../../helpers/bracketData';
import {
  compareScheduleEntries,
  formatScheduleEntry,
  scheduleEntryLabel
} from '../../helpers/roundSchedule';
import { useTitle } from '../../hooks/titleProvider';
import AddGameDialog from '../games/addGameDialog';
import EventSectionCard from '../ui/cards/eventSectionCard';
import GameByStatusCards from '../ui/gameStatusCards/gameByStatusCards';
import AddEditSectionDialog from './addEditSectionDialog';
import AutoGenerateSectionsDialog from './autoGenerateSectionsDialog';
import EventPlayerSetupDialog from './eventPlayerSetupDialog';
import TransferEventDialog from './transferEventDialog';
import VenueSwapDialog from './venueSwapDialog';
import PlayerSwapDialog from '../players/playerSwapDialog';
import EventHistoryDialog from './eventHistoryDialog';

// Icons
import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupsIcon from '@mui/icons-material/Groups';
import HubIcon from '@mui/icons-material/Hub';
import HistoryIcon from '@mui/icons-material/History';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PolicyIcon from '@mui/icons-material/Policy';
import ShareIcon from '@mui/icons-material/Share';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrophyIcon from '@mui/icons-material/EmojiEvents';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PrintIcon from '@mui/icons-material/Print';
import QRCodeIcon from '@mui/icons-material/QrCode';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// Contexts
import { useAuth } from '../../contexts/authContext';
import { Roles, hasWorkspaceAccess } from '../../helpers/access';
import { getEventPlayerOpponentsByRound } from '../../features/event/eventSlice';
import { reportActivity } from '../../features/audit/auditService';
import {
  buildTeamOpponentAutoAdjustments,
  getTeamOpponentsMaxRound
} from './teamOpponentSuggestions';
import { buildGreenSwapSuggestions } from '../greenorganiser/greenSwapSuggestions';

import PrimaryButton from '../ui/buttons/primaryButton';
import PBARankingDialog from './pbaRankingDialog';

const Event = () => {
  let { eventId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { authenticated, currentUserProfile, currentUser, currentWorkspace, workspaces } =
    useAuth();

  const dispatch = useDispatch();
  const event = useSelector((state) => state.event.event);
  const games = useSelector((state) => state.game.games);
  const loading = useSelector((state) => state.event.loading);
  const teamOpponents = useSelector((state) => state.event.playerOpponents);

  const [open, setOpen] = useState(false);
  const [openAddEditSection, setOpenAddEditSection] = useState(false);
  const [openPlayerAdd, setOpenPlayerAdd] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [openNextRoundDialog, setOpenNextRoundDialog] = useState(false);

  const [rounds, setRounds] = useState([]);
  const [sections, setSections] = useState([]);
  const [openPBARanking, setOpenPBARanking] = useState(false);
  const [openTvViewsDialog, setOpenTvViewsDialog] = useState(false);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [openReplacePlayerDialog, setOpenReplacePlayerDialog] = useState(false);
  const [openVenueSwapDialog, setOpenVenueSwapDialog] = useState(false);
  const [openEventHistoryDialog, setOpenEventHistoryDialog] = useState(false);
  const [openExportDrawDialog, setOpenExportDrawDialog] = useState(false);
  const [drawPageMode, setDrawPageMode] = useState('canvas');
  const [exportParts, setExportParts] = useState({
    sections: true,
    bracket: true,
    teamList: true
  });
  const [brackets, setBrackets] = useState([]);
  const [exportingDraw, setExportingDraw] = useState(false);

  const { setTitle } = useTitle();

  const workspaceMemberRole =
    currentWorkspace?.members?.find((member) => member?.id === currentUser?.uid)?.role || '';
  const isWorkspaceAdmin =
    String(workspaceMemberRole).toLowerCase() === 'admin' ||
    currentWorkspace?.ownerId === currentUser?.uid;
  const canManageEventMenu = hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) || isWorkspaceAdmin;
  const canViewEventMenu = authenticated && hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.USER);
  const canSubmitPBARanking = hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SUPERUSER);
  // Transfer rights come from the event's own workspace, which may differ from
  // the one currently selected in the sidebar.
  const eventWorkspace = (workspaces || []).find((ws) => ws.id === event?.workspaceId) || null;
  const eventWorkspaceRole =
    eventWorkspace?.members?.find((member) => member?.id === currentUser?.uid)?.role || '';
  const canTransferEvent =
    Boolean(event?.id) &&
    !event?.deletedAt &&
    Boolean(eventWorkspace) &&
    (String(eventWorkspaceRole).toLowerCase() === 'admin' ||
      eventWorkspace?.ownerId === currentUser?.uid);
  const canManagePortals =
    canTransferEvent || (Boolean(event?.id) && !event?.deletedAt && canSubmitPBARanking);
  // Event-wide corrections need admin rights on the event's own workspace; a
  // superuser acting from any workspace also qualifies.
  const canManageEventChanges =
    Boolean(event?.id) && !event?.deletedAt && (canTransferEvent || canSubmitPBARanking);
  const transferTargets = (workspaces || []).filter((ws) => ws.id !== event?.workspaceId);
  const hasGreens = Boolean(event?.greens?.length);
  const primaryBracket = useMemo(() => pickPrimaryBracket(brackets), [brackets]);
  const hasBracket = Boolean(primaryBracket?.matches?.length);
  const hasExportSelection =
    exportParts.sections || exportParts.bracket || exportParts.teamList;

  const loadExportData = async () => {
    let bracket = null;
    try {
      const result = await dispatch(getBracketsByEventId(eventId)).unwrap();
      bracket = pickPrimaryBracket(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Could not load the bracket for export:', error);
    }

    const gameList = Array.isArray(games) ? games : [];
    if (gameList.length === 0 && !bracket?.matches?.length) {
      dispatch(setAlertError('There is no draw or bracket to export.'));
      return null;
    }

    return { bracket, gameList };
  };

  const reportDrawExport = (format, data) => {
    const partLabels = [
      exportParts.sections ? 'Sections' : null,
      exportParts.bracket ? 'Bracket' : null,
      exportParts.teamList ? 'Team list' : null
    ].filter(Boolean);
    const pageLabel = format === 'pdf' ? ` · ${drawPageMode === 'a4' ? 'A4' : 'Canvas'}` : '';
    const gameCount = Array.isArray(data.gameList) ? data.gameList.length : 0;

    reportActivity(event.id, {
      operation: 'draw.export',
      entityId: event.id,
      entityName: event.name || 'Event',
      summary: `Whole event · ${format === 'xlsx' ? 'XLSX' : 'PDF'}${pageLabel} · ${gameCount} game${
        gameCount === 1 ? '' : 's'
      } · ${partLabels.join(', ')}`,
      metadata: {
        format,
        pageMode: format === 'pdf' ? drawPageMode : null,
        scope: 'event',
        gameCount,
        includeSections: Boolean(exportParts.sections),
        includeBracket: Boolean(exportParts.bracket),
        includeTeamList: Boolean(exportParts.teamList),
        bracketName: data.bracket?.name || null
      }
    });
  };

  const handleExportDrawXlsx = async () => {
    setOpenExportDrawDialog(false);
    if (!event || exportingDraw) return;

    setExportingDraw(true);
    try {
      const data = await loadExportData();
      if (!data) return;
      const { exportDrawXlsx } = await import('../../helpers/drawXlsxExport');
      exportDrawXlsx(event, data.gameList, {
        bracket: data.bracket,
        includeSections: exportParts.sections,
        includeBracket: exportParts.bracket,
        includeTeamList: exportParts.teamList
      });
      reportDrawExport('xlsx', data);
    } catch (error) {
      console.error('Export draw XLSX failed:', error);
      dispatch(setAlertError('The draw could not be exported. Please try again.'));
    } finally {
      setExportingDraw(false);
    }
  };

  const handleExportDrawPdf = async () => {
    setOpenExportDrawDialog(false);
    if (!event || exportingDraw) return;

    setExportingDraw(true);
    try {
      const data = await loadExportData();
      if (!data) return;
      const { exportDrawPdf } = await import('../../helpers/drawPdfExport');
      exportDrawPdf(event, data.gameList, {
        pageMode: drawPageMode,
        bracket: data.bracket,
        includeSections: exportParts.sections,
        includeBracket: exportParts.bracket,
        includeTeamList: exportParts.teamList
      });
      reportDrawExport('pdf', data);
    } catch (error) {
      console.error('Export draw PDF failed:', error);
      dispatch(setAlertError('The draw could not be exported. Please try again.'));
    } finally {
      setExportingDraw(false);
    }
  };

  const teamOpponentConflictCount = useMemo(() => {
    if (!teamOpponents || Object.keys(teamOpponents).length === 0) {
      return 0;
    }

    const maxRound = getTeamOpponentsMaxRound(teamOpponents);
    const suggestions = buildTeamOpponentAutoAdjustments(teamOpponents, maxRound);
    return suggestions.length;
  }, [teamOpponents]);

  const greenSuggestionCount = useMemo(() => {
    if (!event || !hasGreens || !Array.isArray(games) || games.length === 0) {
      return 0;
    }

    const result = buildGreenSwapSuggestions({
      event,
      games,
      selectedRound: 'all',
      avoidTeamsOverusedOnEndRinks: false
    });
    return result?.suggestions?.length || 0;
  }, [event, games, hasGreens]);

  const roundSchedule = useMemo(
    () =>
      (Array.isArray(event?.roundSchedule) ? event.roundSchedule : [])
        .slice()
        .sort(compareScheduleEntries),
    [event?.roundSchedule]
  );

  const eventMenuIconSx = {
    color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.text.primary,
    minWidth: 36
  };

  useEffect(() => {
    console.log('Setting up event listener for event ID:', eventId);
    dispatch(setLoadingEvent(true));
    const docRef = doc(store, 'events', eventId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        console.log('Event data:', docSnap.data());
        const eventData = { id: docSnap.id, ...docSnap.data() };
        dispatch(setEvent(eventData));

        if (eventData.hasSections && !eventData.sections) {
          setOpenAddEditSection(true);
        }

        if (eventData.hasSections && eventData.sections) {
          const sortedSections = [...eventData.sections].sort((a, b) =>
            a.order > b.order ? 1 : -1
          );
          setSections(sortedSections);
        }

        setTitle(eventData.name);
      } else {
        console.log('No such document!');
      }

      dispatch(setLoadingEvent(false));
    });

    return () => unsubscribe();
  }, [eventId, dispatch]);

  useEffect(() => {
    if (event) {
      dispatch(getGameByEventId(event.id));
    }
  }, [dispatch, event]);

  // The bracket lives in its own collection. Fetch it so the export menu can
  // offer bracket-only events too; the export handler re-fetches it fresh.
  useEffect(() => {
    if (!eventId) return undefined;

    let cancelled = false;
    dispatch(getBracketsByEventId(eventId))
      .unwrap()
      .then((result) => {
        if (!cancelled) {
          setBrackets(Array.isArray(result) ? result : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBrackets([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, eventId]);

  useEffect(() => {
    if (eventId && hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN)) {
      dispatch(getEventPlayerOpponentsByRound({ eventId }));
    }
  }, [dispatch, eventId, currentUserProfile]);

  // Check if all games in current round are completed
  const checkGamesCompleted = () => {
    if (!event || !games || games.length === 0) return true;

    const currentRound = event.currentRound || 1;
    const currentRoundGames = games.filter((game) => (game.round || 1) === currentRound);

    if (currentRoundGames.length === 0) return true;

    return !currentRoundGames.every((game) => game.status === 'COMPLETE');
  };

  // Check if we've reached the maximum number of rounds
  const isLastRound = () => {
    if (!event) return true;
    const currentRound = event.currentRound || 1;
    const maxRounds = event.gameFormat?.rounds || 1;

    return currentRound >= maxRounds;
  };

  const handleNextRoundConfirm = () => {
    setOpenNextRoundDialog(true);
  };

  const handleNextRoundCancel = () => {
    setOpenNextRoundDialog(false);
  };

  const handleNextRoundExecute = async () => {
    try {
      await dispatch(
        nextRound({
          eventId: event.id
        })
      ).unwrap();

      setOpenNextRoundDialog(false);
    } catch (error) {
      console.error('Failed to start next round:', error);
      // You might want to show an error message here
    }
  };

  const handleOpenAddGame = (section) => {
    setSelectedSection(section);
    setOpen(true);
  };

  const handlePlayerAddClose = () => {
    setOpenPlayerAdd(false);
  };

  const handleSectionAddEditClose = () => {
    setOpenAddEditSection(false);
  };

  const handleSectionAddEditOpen = (section) => {
    setOpenAddEditSection(true);
  };

  const handleViewSection = (section) => {
    navigate('/events/' + eventId + '/section/' + section.id);
  };

  const handleOpenPBARanking = () => {
    setOpenPBARanking(true);
  };

  const tvLogsLink = `${window.location.origin}/embed/tvlogs/${eventId}`;
  const tvLogsScrollingLink = `${window.location.origin}/embed/tvlogsscrolling/${eventId}`;

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      console.error('Unable to copy link', error);
    }
  };

  const shareLink = async (value, title) => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url: value });
        return;
      }
      await copyToClipboard(value);
    } catch (error) {
      console.error('Unable to share link', error);
    }
  };

  const handleClosePBARanking = () => setOpenPBARanking(false);

  return !event && loading ? (
    <Box
      display="flex"
      flexDirection={'column'}
      height={'100vh'}
      justifyContent="center"
      alignItems="center">
      <CircularProgress size={'60'} color={'primary'} />
    </Box>
  ) : (
    event && (
      <>
        <Stack>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
            {canViewEventMenu ? (
              <Card variant="outlined" sx={{ width: { xs: '100%', lg: 320 }, alignSelf: 'flex-start' }}>
                <CardContent sx={{ p: 0 }}>
                  <List disablePadding>
                    <ListItemButton
                      disabled={!event.hasSections}
                      onClick={() => navigate(`/events/${eventId}/scoreboard`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <TrophyIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Scoreboard" secondary="View event scoreboard" />
                    </ListItemButton>
                    <ListItemButton onClick={() => navigate(`/events/${eventId}/bracket`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <DoubleArrowIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Brackets" secondary="Open event bracket" />
                    </ListItemButton>
                    <ListItemButton
                      disabled={!games?.length}
                      onClick={() => navigate(`/events/${eventId}/games`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <ListAltIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="All Games"
                        secondary="View and filter every game in the event"
                      />
                    </ListItemButton>
                    <EventPlayerSetupDialog
                      readOnly={!canManageEventMenu}
                      canReplacePlayer={canManageEventChanges}
                      canPromoteReserve={canManageEventChanges}
                      trigger={
                        <ListItemButton>
                          <ListItemIcon sx={eventMenuIconSx}>
                            <GroupsIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Players"
                            secondary={canManageEventMenu ? 'Manage players' : 'View players'}
                          />
                        </ListItemButton>
                      }
                    />
                    <ListItemButton onClick={() => navigate(`/events/${eventId}/player-opponents`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <Badge
                          color="error"
                          overlap="circular"
                          badgeContent={teamOpponentConflictCount > 0 ? teamOpponentConflictCount : 0}
                          invisible={teamOpponentConflictCount <= 0}
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}>
                          <HubIcon fontSize="small" />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText
                        primary="Team Opponents Overview"
                        secondary="View opponent summaries"
                      />
                    </ListItemButton>
                    <ListItemButton disabled={!hasGreens} onClick={() => navigate(`/events/${eventId}/greens`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <Badge
                          color="warning"
                          overlap="circular"
                          badgeContent={greenSuggestionCount > 0 ? greenSuggestionCount : 0}
                          invisible={greenSuggestionCount <= 0}
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}>
                          <GridViewIcon fontSize="small" />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary="Greens Overview" secondary="View greens overview" />
                    </ListItemButton>
                    <ListItemButton
                      disabled={exportingDraw || !((hasGreens && games?.length) || hasBracket)}
                      onClick={() => setOpenExportDrawDialog(true)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <TableChartIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Export Draw"
                        secondary="Download the draw and bracket as Excel or PDF"
                      />
                    </ListItemButton>
                    <ListItemButton onClick={() => navigate(`/events/${eventId}/overlays`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <LiveTvIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Broadcasting Overlays"
                        secondary="Open broadcast overlay links"
                      />
                    </ListItemButton>
                    <ListItemButton onClick={() => setOpenTvViewsDialog(true)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <LiveTvIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="TV Views" secondary="Share and copy TV view links" />
                    </ListItemButton>
                    {canManagePortals ? (
                      <ListItemButton onClick={() => navigate(`/events/${eventId}/portals`)}>
                        <ListItemIcon sx={eventMenuIconSx}>
                          <VpnKeyIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Event Portals"
                          secondary="Issue club and secretary scoring PINs"
                        />
                      </ListItemButton>
                    ) : null}
                    {canTransferEvent && transferTargets.length > 0 ? (
                      <ListItemButton onClick={() => setOpenTransferDialog(true)}>
                        <ListItemIcon sx={eventMenuIconSx}>
                          <SwapHorizIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Transfer Event"
                          secondary="Move this event to another workspace"
                        />
                      </ListItemButton>
                    ) : null}
                  </List>

                  {canManageEventChanges ? (
                    <>
                      <Divider />
                      <Box px={2} pt={2} pb={1}>
                        <Typography variant="subtitle2">Bulk Changes</Typography>
                      </Box>
                      <List disablePadding>
                        <ListItemButton onClick={() => setOpenReplacePlayerDialog(true)}>
                          <ListItemIcon sx={eventMenuIconSx}>
                            <PersonSearchIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Replace Player"
                            secondary="Replace one player everywhere in this event"
                          />
                        </ListItemButton>
                        <ListItemButton onClick={() => setOpenVenueSwapDialog(true)}>
                          <ListItemIcon sx={eventMenuIconSx}>
                            <LocationOnIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Swap Venue"
                            secondary="Move every reference to another venue"
                          />
                        </ListItemButton>
                      </List>

                      <Divider />
                      <Box px={2} pt={2} pb={1}>
                        <Typography variant="subtitle2">History</Typography>
                      </Box>
                      <List disablePadding>
                        <ListItemButton onClick={() => setOpenEventHistoryDialog(true)}>
                          <ListItemIcon sx={eventMenuIconSx}>
                            <HistoryIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Event History"
                            secondary="View restore points and undo a bad change"
                          />
                        </ListItemButton>
                        <ListItemButton
                          onClick={() =>
                            navigate(`/events/${encodeURIComponent(eventId)}/audit`)
                          }>
                          <ListItemIcon sx={eventMenuIconSx}>
                            <PolicyIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Audit Log"
                            secondary="See who signed in and changed this event"
                          />
                        </ListItemButton>
                      </List>
                    </>
                  ) : null}

                  <Divider />

                  <Box px={2} pt={2} pb={1}>
                    <Typography variant="subtitle2">QR Codes</Typography>
                  </Box>
                  <List disablePadding>
                    <ListItemButton
                      disabled={!hasGreens}
                      onClick={() => navigate(`/events/${eventId}/greens/printqrcodes`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <PrintIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Print Rink QR Codes" />
                    </ListItemButton>
                    <ListItemButton
                      disabled={!hasGreens}
                      onClick={() => navigate(`/events/${eventId}/sections/printqrcodes`)}>
                      <ListItemIcon sx={eventMenuIconSx}>
                        <QRCodeIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Print Section QR Codes" />
                    </ListItemButton>
                  </List>

                  {canSubmitPBARanking ? (
                    <>
                      <Divider />
                      <List disablePadding>
                        <ListItemButton onClick={handleOpenPBARanking}>
                          <ListItemIcon sx={eventMenuIconSx}>
                            <ChecklistIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Submit to PBA Ranking" />
                        </ListItemButton>
                      </List>
                    </>
                  ) : null}
                </CardContent>
              </Card>
            ) : null}

            <Box display={'flex'} flexDirection={'column'} gap={2} flex={1} minWidth={0}>
              {roundSchedule.length > 0 && (
                <Accordion
                  variant="outlined"
                  disableGutters
                  sx={{ '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="round-times-content"
                    id="round-times-header">
                    <Typography variant="subtitle1" fontWeight={600}>
                      Round Times
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack gap={0.75}>
                      {roundSchedule.map((entry, index) => (
                        <Box
                          key={`${entry.stage || entry.round}-${index}`}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="baseline"
                          gap={2}>
                          <Typography variant="body2" fontWeight={600}>
                            {scheduleEntryLabel(entry)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="right">
                            {formatScheduleEntry(entry)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {event && !event.hasSections && (
                <Box>
                  <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
                    <Typography variant="h4">Games</Typography>
                  </Box>
                  <Box display="flex" flexWrap={'wrap'} gap={2}>
                    {games && games.length > 0 ? (
                      games.map((game) => <GameByStatusCards key={game.id} game={game} />)
                    ) : (
                      <Typography variant="caption">No games found</Typography>
                    )}
                  </Box>
                </Box>
              )}
              {event && event.hasSections && (
                <Box>
                  <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2} gap={3}>
                    <Typography variant="h4">Sections</Typography>
                    {canManageEventMenu ? (
                      <>
                        <AutoGenerateSectionsDialog event={event} />
                        {event.roundPublishMethod === 'publish-rounds' && (
                          <PrimaryButton
                            variant="contained"
                            onClick={handleNextRoundConfirm}
                            disabled={checkGamesCompleted() || isLastRound() ? false : true}
                            color={'info'}
                            startIcon={<PlayArrowIcon />}>
                            {isLastRound()
                              ? 'Final Round'
                              : `Start Round ${(event.currentRound || 1) + 1}`}
                          </PrimaryButton>
                        )}
                      </>
                    ) : null}
                  </Box>
                  <Box display="flex" flexWrap={'wrap'}>
                    {sections?.map((section) => (
                      <EventSectionCard
                        key={section.id}
                        section={section}
                        event={event}
                        onAddGame={() => handleOpenAddGame(section)}
                        onViewSection={() => handleViewSection(section)}
                        onUpdateSection={() => handleSectionAddEditOpen(section)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Stack>

          <AddEditSectionDialog
            open={openAddEditSection}
            onClose={() => handleSectionAddEditClose()}
            event={event}
          />
        </Stack>

        {/* Next Round Confirmation Dialog */}
        <Dialog
          open={openNextRoundDialog}
          onClose={handleNextRoundCancel}
          aria-labelledby="next-round-dialog-title"
          aria-describedby="next-round-dialog-description">
          <DialogTitle id="next-round-dialog-title">Start Next Round</DialogTitle>
          <DialogContent>
            <DialogContentText id="next-round-dialog-description">
              Are you sure you want to start Round {(event.currentRound || 1) + 1}?
              <br />
              <br />
              This will make the next round visible to all participants and update the leaderboard
              to include games from Round {(event.currentRound || 1) + 1}.
              <br />
              <br />
              <strong>Current Round:</strong> {event.currentRound || 1}
              <br />
              <strong>Next Round:</strong> {(event.currentRound || 1) + 1}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNextRoundCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleNextRoundExecute} color="primary" variant="contained" autoFocus>
              Start Round {(event.currentRound || 1) + 1}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Export Draw Dialog */}
        <Dialog
          open={openExportDrawDialog}
          onClose={() => setOpenExportDrawDialog(false)}
          maxWidth="xs"
          fullWidth
          aria-labelledby="export-draw-dialog-title"
          aria-describedby="export-draw-dialog-description">
          <DialogTitle id="export-draw-dialog-title">Export Draw</DialogTitle>
          <DialogContent>
            <DialogContentText id="export-draw-dialog-description">
              Choose what to include, then a format to download.
            </DialogContentText>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={exportParts.sections}
                    onChange={(event) =>
                      setExportParts((prev) => ({ ...prev, sections: event.target.checked }))
                    }
                  />
                }
                label="Sections"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={exportParts.bracket}
                    disabled={!hasBracket}
                    onChange={(event) =>
                      setExportParts((prev) => ({ ...prev, bracket: event.target.checked }))
                    }
                  />
                }
                label="Bracket"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={exportParts.teamList}
                    onChange={(event) =>
                      setExportParts((prev) => ({ ...prev, teamList: event.target.checked }))
                    }
                  />
                }
                label="Team List"
              />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {hasBracket
                ? `Bracket: ${primaryBracket?.name || 'Bracket'}`
                : 'No bracket found for this event.'}
            </Typography>
            {!hasExportSelection && (
              <Typography variant="caption" color="warning.main" display="block">
                Choose at least one part to export.
              </Typography>
            )}
            <Stack spacing={1.5} mt={2}>
              <Button
                fullWidth
                variant="outlined"
                disabled={exportingDraw || !hasExportSelection}
                startIcon={<TableChartIcon />}
                onClick={handleExportDrawXlsx}>
                Excel (.xlsx)
              </Button>
              <Box>
                <Typography variant="subtitle2">PDF page size</Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={drawPageMode}
                  onChange={(event, value) => value && setDrawPageMode(value)}
                  aria-label="PDF page size"
                  sx={{ mt: 0.5 }}>
                  <ToggleButton value="canvas">Canvas</ToggleButton>
                  <ToggleButton value="a4">A4</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Button
                fullWidth
                variant="outlined"
                disabled={exportingDraw || !hasExportSelection}
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportDrawPdf}>
                PDF (.pdf)
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenExportDrawDialog(false)} color="primary">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ position: 'relative' }}>
          <Box display="flex" gap={1} sx={{ position: 'fixed', bottom: 64, right: 16 }}>
            {hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
              <>
                {event.hasSections ? (
                  <Fab color="info" aria-label="add" onClick={() => handleSectionAddEditOpen()}>
                    <AddIcon />
                  </Fab>
                ) : (
                  <AddGameDialog event={event} fab />
                )}
              </>
            )}
          </Box>
        </Box>

        <PBARankingDialog open={openPBARanking} onClose={handleClosePBARanking} />

        <TransferEventDialog
          open={openTransferDialog}
          onClose={() => setOpenTransferDialog(false)}
          event={event}
          targets={transferTargets}
          sourceWorkspaceName={eventWorkspace?.name}
          onTransferred={() => {
            setOpenTransferDialog(false);
            navigate('/events');
          }}
        />

        <PlayerSwapDialog
          open={openReplacePlayerDialog}
          onClose={() => setOpenReplacePlayerDialog(false)}
          eventId={eventId}
        />

        <VenueSwapDialog
          open={openVenueSwapDialog}
          onClose={() => setOpenVenueSwapDialog(false)}
          eventId={eventId}
        />

        <EventHistoryDialog
          open={openEventHistoryDialog}
          onClose={() => setOpenEventHistoryDialog(false)}
          eventId={eventId}
          eventName={event?.name}
          onRestored={() => {
            dispatch(getGameByEventId({ eventId, includePhotos: false }));
            dispatch(getBracketsByEventId(eventId))
              .unwrap()
              .then((result) => setBrackets(Array.isArray(result) ? result : []))
              .catch(() => setBrackets([]));
          }}
        />

        <Dialog open={openTvViewsDialog} onClose={() => setOpenTvViewsDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>TV Views</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <Box>
                <Typography variant="subtitle2">TV Logs</Typography>
                <MuiLink href={tvLogsLink} target="_blank" rel="noreferrer" sx={{ wordBreak: 'break-all' }}>
                  {tvLogsLink}
                </MuiLink>
                <Stack direction="row" spacing={1} mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(tvLogsLink)}>
                    Copy
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => shareLink(tvLogsLink, 'TV Logs')}>
                    Share
                  </Button>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2">TV Logs Scrolling</Typography>
                <MuiLink
                  href={tvLogsScrollingLink}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ wordBreak: 'break-all' }}>
                  {tvLogsScrollingLink}
                </MuiLink>
                <Stack direction="row" spacing={1} mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(tvLogsScrollingLink)}>
                    Copy
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => shareLink(tvLogsScrollingLink, 'TV Logs Scrolling')}>
                    Share
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenTvViewsDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    )
  );
};

export default Event;
